import { ExternalLink } from 'lucide-react';

import type { PolicyFitStatus } from '@ballerine/common';
import { POLICY_FIT_STATUS_MAP } from '@ballerine/common';

import { ctw } from '@/common';
import { ContentTooltip } from '@/components/molecules';
import { QuestionMarkTooltip } from '../QuestionMarkTooltip';
import { BadgeCheck } from '../svg/BadgeCheck';
import { BadgeCritical } from '../svg/BadgeCritical';
import { BadgeUnknown } from '../svg/BadgeUnknown';
import { BadgeWarn } from '../svg/BadgeWarn';

type PolicyFitProps = {
  policyFit: {
    status?: PolicyFitStatus;
    description?: string | null;
    displayName?: string | null;
    version?: number | null;
    policyFitSnippet?: string | null;
    originalPolicyFileId?: string | null;
  };
};

// Shared status → label map. Exported so consumers (e.g. the backoffice Home
// and Monitoring Alerts policy-fit widgets) reuse one source of truth instead
// of each re-declaring the labels.
export const policyFitTextMap: Record<PolicyFitStatus, string> = {
  [POLICY_FIT_STATUS_MAP.NEUTRAL]: 'Neutral',
  [POLICY_FIT_STATUS_MAP.RESTRICTED]: 'Restricted',
  [POLICY_FIT_STATUS_MAP.PROHIBITED]: 'Prohibited',
  [POLICY_FIT_STATUS_MAP.UNVERIFIED]: 'Unverified',
};

const getPolicyFitStyles = (status: PolicyFitStatus | null, variant: 'default' | 'simple') => {
  switch (status) {
    case POLICY_FIT_STATUS_MAP.NEUTRAL:
      return { icon: <BadgeCheck variant={variant} />, className: 'bg-green-500' };
    case POLICY_FIT_STATUS_MAP.RESTRICTED:
      return { icon: <BadgeWarn variant={variant} />, className: 'bg-orange-300' };
    case POLICY_FIT_STATUS_MAP.PROHIBITED:
      return { icon: <BadgeCritical variant={variant} />, className: 'bg-red-500' };
    default:
      return {
        icon: <BadgeUnknown variant={variant} />,
        className: 'bg-zinc-100 text-neutral-400',
        iconClassName: 'mb-0',
      };
  }
};

export const PolicyFitIcon = ({
  policyFit,
  variant,
  withStatusText = false,
  withTooltip = false,
}: PolicyFitProps & {
  variant: 'default' | 'simple';
  withStatusText?: boolean;
  withTooltip?: boolean;
}) => {
  const { status = POLICY_FIT_STATUS_MAP.UNVERIFIED, policyFitSnippet } = policyFit;
  const { icon } = getPolicyFitStyles(status, variant);

  const statusText = policyFitTextMap[status];

  const iconElement = withTooltip ? (
    <button
      type="button"
      className="flex cursor-pointer items-baseline gap-2 border-none bg-transparent p-0"
      aria-label={`Policy fit: ${statusText}`}
    >
      <div className={ctw('-mb-2')}>{icon}</div>
      {withStatusText && <span className="text-slate-800">{statusText}</span>}
    </button>
  ) : (
    <div className="flex items-baseline gap-2">
      <div className={ctw('-mb-2')}>{icon}</div>
      {withStatusText && <span className="text-slate-800">{statusText}</span>}
    </div>
  );

  if (!withTooltip) {
    return iconElement;
  }

  return (
    <ContentTooltip
      description={
        <div className="space-y-2">
          <PolicyFitBadge policyFit={policyFit} />
          {policyFitSnippet && <p className="text-sm text-zinc-600">{policyFitSnippet}</p>}
        </div>
      }
      props={{
        tooltipTrigger: { className: 'pr-0' },
        tooltipContent: {
          align: 'center',
          side: 'bottom',
          sideOffset: 25,
          className: 'bg-background text-primary max-w-[320px]',
        },
      }}
    >
      {iconElement}
    </ContentTooltip>
  );
};

export const PolicyFitBadge = ({ policyFit }: PolicyFitProps) => {
  const { status = POLICY_FIT_STATUS_MAP.UNVERIFIED } = policyFit;
  const { className } = getPolicyFitStyles(status, 'default');

  return (
    <div className={ctw('w-fit rounded-lg px-2 py-1 font-semibold text-white', className)}>
      <div className="flex items-center gap-1">
        <PolicyFitIcon policyFit={policyFit} variant="default" />
        <span>{policyFitTextMap[status]}</span>
      </div>
    </div>
  );
};

type PolicyFitHeaderProps = PolicyFitProps & {
  policy?: {
    name?: string | null;
  } | null;
  onOpenOriginalPolicy?: () => void;
};

export const PolicyFitHeader = ({
  policyFit,
  policy,
  onOpenOriginalPolicy,
}: PolicyFitHeaderProps) => {
  const { version, originalPolicyFileId } = policyFit;
  // BAL-7323: never fall back to the customer's display name when policy.name
  // is missing — for multi-policy customers (or API uploads with no policyName)
  // that would be actively misleading. Hide the "Based on …" line instead.
  const displayName = policy?.name ?? null;
  const showOriginalPolicyLink = originalPolicyFileId && onOpenOriginalPolicy;

  return (
    <div className="flex-1">
      <div className="mb-3 flex items-center gap-1">
        <h4 className="text-sm font-semibold text-stone-400">YOUR POLICY FIT</h4>
        <QuestionMarkTooltip
          text="Assessment of how well this merchant aligns with your underwriting policies and risk acceptance criteria."
          className="ml-2"
        />
      </div>

      <PolicyFitBadge policyFit={policyFit} />

      {displayName && version != null && (
        <p className="mt-1 flex items-center gap-1 text-sm text-stone-400">
          <span>
            Based on {displayName} v.{version}
          </span>
          {showOriginalPolicyLink && (
            <button
              type="button"
              onClick={onOpenOriginalPolicy}
              className="inline-flex items-center text-purple-600 hover:text-purple-700"
              title="View original policy document"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          )}
        </p>
      )}
    </div>
  );
};
